import { DEFAULT_RETRYABLE, isApiErrorDetails, HTTPS_TO_APP_ERROR, CALLABLE_NAMES, CALLABLES, SUBSCRIPTIONS, getCallable, API_VERSION } from '@levelup/api-contract';
import { ZodError } from 'zod';
import { v7 } from 'uuid';

// src/create-client.ts
var ApiError = class _ApiError extends Error {
  constructor(init) {
    super(init.message);
    this.name = "ApiError";
    this.code = init.code;
    this.retryable = init.retryable ?? defaultRetryable(init.code);
    this.validationErrors = init.validationErrors;
    this.meta = init.meta;
    this.cause = init.cause;
    this.callable = init.callable;
    this.httpsCode = init.httpsCode;
    Object.setPrototypeOf(this, _ApiError.prototype);
  }
  /** The stable, serializable details shape (mirrors `ApiErrorDetails` + callable). */
  toJSON() {
    return {
      code: this.code,
      message: this.message,
      ...this.validationErrors ? { validationErrors: this.validationErrors } : {},
      retryable: this.retryable,
      ...this.meta ? { meta: this.meta } : {},
      ...this.callable ? { callable: this.callable } : {}
    };
  }
};
function isApiError(e) {
  return e instanceof ApiError;
}
function defaultRetryable(code) {
  if (code === "NETWORK_ERROR") return true;
  if (code === "UNKNOWN") return false;
  return DEFAULT_RETRYABLE[code];
}
function fromZodError(e, callable) {
  const validationErrors = e.issues.map((issue) => ({
    path: issue.path.map((p) => String(p)).join("."),
    message: issue.message
  }));
  return new ApiError({
    code: "VALIDATION_ERROR",
    message: "The request contains invalid data.",
    retryable: false,
    validationErrors,
    cause: e,
    callable
  });
}
function bareFirebaseCode(code) {
  const slash = code.lastIndexOf("/");
  return slash >= 0 ? code.slice(slash + 1) : code;
}
function isFunctionsErrorCode(code) {
  return code in HTTPS_TO_APP_ERROR;
}
function asRecord(e) {
  return e && typeof e === "object" ? e : void 0;
}
function coerceMeta(meta) {
  return meta && typeof meta === "object" ? meta : void 0;
}
function fromTransportError(e, callable) {
  const obj = asRecord(e);
  const rawMessage = obj && typeof obj["message"] === "string" ? obj["message"] : void 0;
  const details = obj?.["details"];
  if (isApiErrorDetails(details)) {
    const rawCode2 = obj && typeof obj["code"] === "string" ? obj["code"] : void 0;
    return new ApiError({
      code: details.code,
      message: details.message ?? rawMessage ?? "An unexpected error occurred. Please try again.",
      retryable: details.retryable ?? DEFAULT_RETRYABLE[details.code],
      validationErrors: details.validationErrors,
      meta: details.meta,
      cause: e,
      callable,
      ...rawCode2 ? { httpsCode: rawCode2 } : {}
    });
  }
  const rawCode = obj && typeof obj["code"] === "string" ? obj["code"] : void 0;
  if (rawCode) {
    const bare = bareFirebaseCode(rawCode);
    if (isFunctionsErrorCode(bare)) {
      const appCode = HTTPS_TO_APP_ERROR[bare];
      return new ApiError({
        code: appCode,
        message: rawMessage ?? "An unexpected error occurred. Please try again.",
        retryable: DEFAULT_RETRYABLE[appCode],
        meta: coerceMeta(obj?.["meta"]),
        cause: e,
        callable,
        httpsCode: rawCode
      });
    }
  }
  return new ApiError({
    code: "UNKNOWN",
    message: rawMessage ?? "An unexpected error occurred. Please try again.",
    retryable: false,
    cause: e,
    callable,
    ...rawCode ? { httpsCode: rawCode } : {}
  });
}
function isNetworkError(e) {
  if (e instanceof TypeError) return true;
  const obj = asRecord(e);
  const name = obj && typeof obj["name"] === "string" ? obj["name"] : void 0;
  if (name === "AbortError" || name === "TimeoutError") return true;
  const hasCode = obj && typeof obj["code"] === "string";
  const msg = obj && typeof obj["message"] === "string" ? obj["message"] : "";
  return !hasCode && /network|fetch|offline|connection/i.test(msg);
}
function normalizeError(e, callable) {
  if (isApiError(e)) {
    if (callable && !e.callable) e.callable = callable;
    return e;
  }
  if (e instanceof ZodError) {
    return fromZodError(e, callable);
  }
  const obj = asRecord(e);
  if (isApiErrorDetails(obj?.["details"])) {
    return fromTransportError(e, callable);
  }
  if (obj && typeof obj["code"] === "string") {
    return fromTransportError(e, callable);
  }
  if (isNetworkError(e)) {
    return new ApiError({
      code: "NETWORK_ERROR",
      message: e instanceof Error ? e.message : "A network error occurred.",
      retryable: true,
      cause: e,
      callable
    });
  }
  return new ApiError({
    code: "UNKNOWN",
    message: e instanceof Error ? e.message : "An unexpected error occurred. Please try again.",
    retryable: false,
    cause: e,
    callable
  });
}

// src/envelope.ts
var API_VERSION_ENVELOPE_KEY = "__apiVersion";
function withApiVersion(req, _def, apiVersion) {
  return {
    ...req,
    [API_VERSION_ENVELOPE_KEY]: apiVersion
  };
}
var IDEMPOTENCY_ENVELOPE_KEY = "__idempotencyKey";
var generateIdempotencyKey = (_name) => v7();
function attachIdempotencyKey(_name, req, _def, key) {
  return {
    ...req,
    [IDEMPOTENCY_ENVELOPE_KEY]: key
  };
}
function operationOf(name) {
  return name.split(".").slice(2).join(".");
}
function buildNamespaces(call) {
  const ns = {};
  for (const name of CALLABLE_NAMES) {
    const def = CALLABLES[name];
    const mod = def.module;
    const op = operationOf(name);
    (ns[mod] ?? (ns[mod] = {}))[op] = call(name);
  }
  return ns;
}

// src/offline.ts
var NoopOfflineQueue = class {
  constructor() {
    this.status = "idle";
  }
  async enqueue(call) {
    if (call.deliver) return call.deliver();
    return void 0;
  }
  async flush() {
  }
};
function routeThroughQueue(queue, name, data, key, deliver) {
  if (!queue) return deliver();
  return queue.enqueue({
    name,
    data,
    idempotencyKey: key,
    enqueuedAt: Date.now(),
    deliver
  });
}
function asNext(cb) {
  return typeof cb === "function" ? cb : cb.next;
}
function withCallbacks(cb, next) {
  if (typeof cb === "function") return next;
  return { ...cb, next };
}
function makeSubscribe(transport, opts = {}) {
  return (name, params, cb) => {
    const userNext = asNext(cb);
    if (!opts.validateResponses) {
      return transport.subscribe(name, params, cb);
    }
    const def = SUBSCRIPTIONS[name];
    const validatingNext = (payload) => {
      def.payload.parse(payload);
      userNext(payload);
    };
    return transport.subscribe(name, params, withCallbacks(cb, validatingNext));
  };
}

// src/retry.ts
function isApiErrorShape(e) {
  if (e instanceof ApiError) return true;
  if (!e || typeof e !== "object") return false;
  const o = e;
  return typeof o["code"] === "string" && typeof o["retryable"] === "boolean";
}
var DEFAULT_RETRY_POLICY = {
  maxAttempts: 3,
  baseDelayMs: 200,
  maxDelayMs: 4e3,
  jitter: "full"
};
var NO_RETRY = {
  maxAttempts: 1,
  baseDelayMs: 0,
  maxDelayMs: 0,
  jitter: "none"
};
function isRetryable(err, def) {
  if (!err.retryable) return false;
  return def.idempotent === true || def.rateTier === "read";
}
function computeBackoff(attempt, policy, rand = Math.random, retryAfterMs) {
  const cap = policy.maxDelayMs;
  if (typeof retryAfterMs === "number" && Number.isFinite(retryAfterMs)) {
    return Math.max(0, Math.min(retryAfterMs, cap));
  }
  const exp = policy.baseDelayMs * Math.pow(2, Math.max(0, attempt));
  const ceiling = Math.min(exp, cap);
  if (policy.jitter === "none") return ceiling;
  return Math.max(0, Math.min(ceiling, ceiling * clamp01(rand())));
}
function clamp01(x) {
  if (!Number.isFinite(x)) return 0;
  return x < 0 ? 0 : x > 1 ? 1 : x;
}
var defaultSleep = (ms) => ms <= 0 ? Promise.resolve() : new Promise((resolve) => setTimeout(resolve, ms));
function retryAfterOf(err) {
  const v = err.meta?.["retryAfterMs"];
  return typeof v === "number" ? v : void 0;
}
async function withRetry(attempt, policy, ctx) {
  const sleep = ctx.sleep ?? defaultSleep;
  const rand = ctx.rand ?? Math.random;
  const decide = policy.shouldRetry ?? ((err, def) => isRetryable(err, def));
  const maxAttempts = Math.max(1, policy.maxAttempts);
  let lastErr;
  for (let i = 0; i < maxAttempts; i++) {
    try {
      return await attempt(i);
    } catch (raw) {
      const err = isApiError(raw) ? raw : isApiErrorShape(raw) ? raw : normalizeError(raw, ctx.def.name);
      lastErr = err;
      const isLast = i >= maxAttempts - 1;
      if (isLast || !decide(err, ctx.def, i)) throw err;
      const delay = computeBackoff(i, policy, rand, retryAfterOf(err));
      void ctx.now();
      await sleep(delay);
    }
  }
  throw lastErr ?? new ApiError({ code: "UNKNOWN", message: "retry exhausted" });
}
function stripUndefinedDeep(value) {
  if (Array.isArray(value)) {
    return value.map((v) => stripUndefinedDeep(v));
  }
  if (value !== null && typeof value === "object") {
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      if (v !== void 0) out[k] = stripUndefinedDeep(v);
    }
    return out;
  }
  return value;
}
function validateRequest(name, data) {
  const def = getCallable(name);
  try {
    return stripUndefinedDeep(def.requestSchema.parse(data));
  } catch (e) {
    throw normalizeError(e, name);
  }
}
function validateResponse(name, res, enabled) {
  if (!enabled) return res;
  const def = getCallable(name);
  try {
    return def.responseSchema.parse(res);
  } catch (e) {
    throw normalizeError(e, name);
  }
}

// src/create-client.ts
function createApiClient(transport, opts = {}) {
  const apiVersion = opts.apiVersion ?? API_VERSION;
  const makeKey = opts.getIdempotencyKey ?? generateIdempotencyKey;
  const policy = opts.retry === false ? NO_RETRY : opts.retry ?? DEFAULT_RETRY_POLICY;
  const now = opts.now ?? Date.now;
  function call(name) {
    const def = CALLABLES[name];
    return async (data) => {
      try {
        const req = validateRequest(name, data);
        const key = def.idempotent ? makeKey(name) : void 0;
        const versioned = withApiVersion(req, def, apiVersion);
        const envelope = key ? attachIdempotencyKey(name, versioned, def, key) : versioned;
        const deliver = async () => validateResponse(
          name,
          await transport.invoke(name, envelope),
          !!opts.validateResponses
        );
        const send = def.idempotent && opts.offlineQueue && key ? () => routeThroughQueue(opts.offlineQueue, name, req, key, deliver) : deliver;
        return await withRetry(() => send(), policy, { def, now });
      } catch (e) {
        const err = normalizeError(e, name);
        try {
          opts.onError?.(err, name);
        } catch {
        }
        throw err;
      }
    };
  }
  const namespaces = buildNamespaces(call);
  return {
    ...namespaces,
    subscribe: makeSubscribe(transport, { validateResponses: opts.validateResponses }),
    call,
    // DP-1 (TR-2 runtime-bug fix): expose the transport's Storage capability so
    // `api.storage.*` is defined (storageRepo would TypeError on first upload otherwise).
    storage: transport.storage
  };
}

export { API_VERSION_ENVELOPE_KEY, ApiError, DEFAULT_RETRY_POLICY, IDEMPOTENCY_ENVELOPE_KEY, NO_RETRY, NoopOfflineQueue, attachIdempotencyKey, buildNamespaces, computeBackoff, createApiClient, fromTransportError, fromZodError, generateIdempotencyKey, isApiError, isRetryable, makeSubscribe, normalizeError, operationOf, routeThroughQueue, stripUndefinedDeep, validateRequest, validateResponse, withApiVersion, withRetry };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map